import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';

// Default to port 3001 for backend API
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Create axios instance
const baseApiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter function - will be set by the hook wrapper
let getTokenFn: (() => Promise<string | null>) | null = null;

// Request deduplication: track in-flight requests to prevent duplicate calls
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Generate a unique key for request deduplication
 */
function getRequestKey(method: string, url: string): string {
  return `${method.toUpperCase()}:${url}`;
}

/**
 * Deduplicate requests - if the same request is already in flight, return the existing promise
 */
function deduplicateRequest<T>(method: string, url: string, requestFn: () => Promise<T>): Promise<T> {
  const requestKey = getRequestKey(method, url);
  const pendingRequest = pendingRequests.get(requestKey);
  
  if (pendingRequest) {
    // Return the existing promise
    return pendingRequest as Promise<T>;
  }
  
  // Create new request and track it
  const requestPromise = requestFn()
    .finally(() => {
      // Clean up when request completes (success or error)
      pendingRequests.delete(requestKey);
    });
  
  pendingRequests.set(requestKey, requestPromise);
  return requestPromise;
}

/**
 * Set the token getter function (called from hooks that have access to useAuth)
 */
export function setTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

// Request interceptor - add auth token
baseApiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get token using the provided getter function
      if (getTokenFn && config.headers) {
        const token = await getTokenFn();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      // If token retrieval fails, continue without token
      // The backend will return 401 if authentication is required
      console.warn('Failed to get auth token:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
baseApiClient.interceptors.response.use(
  (response) => {
    // Extract data from response (backend returns { success, data, meta })
    if (response.data && response.data.success !== undefined) {
      return response.data.data || response.data;
    }
    return response.data;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error
      const errorData = error.response.data as any;
      const errorResponse = {
        code: errorData.error?.code || 'UNKNOWN_ERROR',
        message: errorData.error?.message || 'An error occurred',
        details: errorData.error?.details,
        status: error.response.status,
      };
      return Promise.reject(errorResponse);
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
        status: 0,
      });
    } else {
      // Error setting up request
      return Promise.reject({
        code: 'REQUEST_ERROR',
        message: error.message || 'An error occurred',
        status: 0,
      });
    }
  }
);

// Endpoints that should be deduplicated to prevent duplicate calls during login/navigation
const DEDUPLICATE_ENDPOINTS = ['/users/me', '/profiles/client/me'];

// Create a wrapped API client that deduplicates GET requests to specific endpoints
const apiClient = new Proxy(baseApiClient, {
  get(target, prop) {
    const originalMethod = target[prop as keyof AxiosInstance];
    
    // Only wrap the 'get' method to deduplicate specific endpoint requests
    if (prop === 'get' && typeof originalMethod === 'function') {
      return function(url: string, config?: AxiosRequestConfig): Promise<any> {
        // Deduplicate GET requests to endpoints that are commonly called multiple times
        if (DEDUPLICATE_ENDPOINTS.includes(url)) {
          return deduplicateRequest('get', url, () => (originalMethod as any).call(target, url, config));
        }
        return (originalMethod as any).call(target, url, config);
      };
    }
    
    return originalMethod;
  }
}) as AxiosInstance;

export default apiClient;

